// Top app bar — chrome ONLY.
//
// Layout (left → right):
//   - mobile sidebar toggle (hamburger; visible <= 768px)
//   - TKOS amber brand badge "the kitchen os" + "ADMIN" tag
//   - flex spacer
//   - current-retailer filter pill (only shown when set; click to clear)
//   - user email
//   - sign out button

import { logout, onAuthChange, getCachedUser } from "./auth";
import type { AdminUser } from "./types";

export interface HeaderOptions {
  /** Called when the mobile hamburger is tapped. */
  onToggleNav: () => void;
  /** Called after a successful logout so the shell can re-render login. */
  onLoggedOut: () => void;
  /**
   * Live read of the currently-selected retailer filter (set by the Retailers
   * page). Returning `{ id, label }` shows the pill; `null` hides it.
   */
  getRetailerFilter: () => { id: string; label: string } | null;
  /** Clear the retailer filter (called when the user clicks the pill's X). */
  clearRetailerFilter: () => void;
  /** Subscribe to changes in the retailer filter; returns unsubscribe. */
  onRetailerFilterChange: (fn: () => void) => () => void;
}

export interface HeaderHandle {
  el: HTMLElement;
  destroy: () => void;
}

export function createHeader(opts: HeaderOptions): HeaderHandle {
  const header = document.createElement("header");
  header.className = "adm-header";
  header.setAttribute("role", "banner");

  // Hamburger (mobile only — CSS hides on desktop).
  const hamBtn = document.createElement("button");
  hamBtn.type = "button";
  hamBtn.className = "adm-header__ham";
  hamBtn.setAttribute("aria-label", "Toggle navigation");
  hamBtn.innerHTML = `
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <line x1="3" y1="12" x2="21" y2="12"></line>
      <line x1="3" y1="18" x2="21" y2="18"></line>
    </svg>
  `;
  hamBtn.addEventListener("click", opts.onToggleNav);

  // Brand mark — TKOS amber badge.
  const brand = document.createElement("a");
  brand.className = "adm-header__brand";
  brand.href = "#/dashboard";
  brand.innerHTML = `
    <span class="adm-header__badge">TKOS</span>
    <span class="adm-header__brand-tag">Admin</span>
  `;

  // Spacer.
  const spacer = document.createElement("div");
  spacer.className = "adm-header__spacer";

  // Retailer filter pill — hidden by default; rendered live from the callback.
  const filterPill = document.createElement("button");
  filterPill.type = "button";
  filterPill.className = "adm-header__retailer-pill";
  filterPill.setAttribute("aria-label", "Clear retailer filter");
  filterPill.title = "Clear retailer filter";
  filterPill.style.display = "none";
  filterPill.addEventListener("click", () => {
    opts.clearRetailerFilter();
  });

  const renderFilterPill = (): void => {
    const f = opts.getRetailerFilter();
    if (f) {
      filterPill.style.display = "";
      filterPill.innerHTML = `
        <span class="adm-header__retailer-pill-label">Retailer:</span>
        <strong>${escapeHtml(f.label)}</strong>
        <span class="adm-header__retailer-pill-x" aria-hidden="true">×</span>
      `;
    } else {
      filterPill.style.display = "none";
      filterPill.replaceChildren();
    }
  };
  renderFilterPill();
  const offFilter = opts.onRetailerFilterChange(renderFilterPill);

  // User menu.
  const userMenu = document.createElement("div");
  userMenu.className = "adm-header__user";

  const userEmail = document.createElement("span");
  userEmail.className = "adm-header__user-email";

  const signOutBtn = document.createElement("button");
  signOutBtn.type = "button";
  signOutBtn.className = "adm-header__signout";
  signOutBtn.textContent = "Sign out";
  signOutBtn.addEventListener("click", async () => {
    if (!confirm("Sign out of the admin portal?")) return;
    signOutBtn.disabled = true;
    signOutBtn.textContent = "Signing out…";
    try {
      await logout();
    } finally {
      opts.onLoggedOut();
    }
  });

  userMenu.append(userEmail, signOutBtn);

  const renderUser = (user: AdminUser | null): void => {
    if (user) {
      userEmail.textContent = user.email;
      userMenu.style.display = "";
    } else {
      userEmail.textContent = "";
      userMenu.style.display = "none";
    }
  };
  renderUser(getCachedUser());
  const offAuth = onAuthChange(renderUser);

  header.append(hamBtn, brand, spacer, filterPill, userMenu);

  return {
    el: header,
    destroy: () => {
      offAuth();
      offFilter();
    },
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
